/** Document properties: category, who can see it, publishing (/pub/:slug), share links, graph extraction, dates */

import { useEffect, useId, useState, type FormEvent } from 'react'
import { toast } from 'sonner'
import { Calendar, Check, Copy, ExternalLink, Globe, User } from 'lucide-react'
import { ACCESS_LEVELS, type AccessLevel } from '@agentwiki/shared'
import { useDocument, useUpdateDocument } from '../../hooks/use-documents'
import { publishedDocUrl, usePublishDocument, useUnpublishDocument } from '../../hooks/use-share-links'
import { useAppStore } from '../../stores/app-store'
import { useT } from '../../i18n'
import { cn } from '../../lib/utils'
import { ACCESS_LEVEL_LABEL_KEYS } from '../documents/access-level-badge'
import { Modal } from '../ui/modal'
import { buttonPrimaryClass, buttonSecondaryClass, errorTextClass, hintClass, iconButtonClass, inputClass, labelClass } from '../ui/styles'
import { ShareLinkDisplay } from './share-link-display'
import { DocumentKnowledgeExtractionPanel } from './document-knowledge-extraction-panel'


const DESCRIPTION_MAX = 1000

const sectionTitleClass = 'text-xs font-semibold uppercase tracking-wider text-fg-subtle'

interface DocumentPropertiesProps {
  documentId: string
  /** Ignored: styling follows the theme tokens. Kept so older call sites still compile. */
  isDark?: boolean
  /**
   * While the page is being edited: category, access and publish changes would move the page's updatedAt and turn
   * the editor's own save into a conflict, so they wait until the edit is saved or cancelled
   */
  readOnly?: boolean
}

function errorMessage(err: unknown): string {
  return err instanceof Error ? err.message : String(err)
}

/** Formatted date, or null when the value is missing or not a valid date (the cache can briefly hold a partial document) */
function formatDateSafe(format: Intl.DateTimeFormat, value: Date | string | number | null | undefined): string | null {
  if (value === null || value === undefined || value === '') return null
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : format.format(date)
}

function isAccessLevel(value: unknown): value is AccessLevel {
  return typeof value === 'string' && (ACCESS_LEVELS as readonly string[]).includes(value)
}

export function DocumentProperties({ documentId, readOnly = false }: DocumentPropertiesProps) {
  const t = useT()
  const id = useId()
  const locale = useAppStore((s) => s.locale)
  // The graph extraction panel is legacy (not yet on tokens) and still takes the resolved theme
  const legacyIsDark = useAppStore((s) => s.theme) === 'dark'
  const { data: doc } = useDocument(documentId)
  const updateDocument = useUpdateDocument()
  const publishDocument = usePublishDocument()
  const unpublishDocument = useUnpublishDocument()

  const [category, setCategory] = useState(doc?.category ?? '')
  const [publishOpen, setPublishOpen] = useState(false)
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    if (doc) setCategory(doc.category ?? '')
  }, [doc])

  if (!doc) return null

  // A document seeded into the cache from a create response lacks access level and dates until it is refetched
  const accessLevel = isAccessLevel(doc.accessLevel) ? doc.accessLevel : undefined
  const isPublished = accessLevel === 'public'
  const publicUrl = publishedDocUrl(doc.slug, window.location.origin)
  const dateFormat = new Intl.DateTimeFormat(locale, { dateStyle: 'medium' })
  const busy = updateDocument.isPending || publishDocument.isPending || unpublishDocument.isPending || !accessLevel
  const created = formatDateSafe(dateFormat, doc.createdAt)
  const updated = formatDateSafe(dateFormat, doc.updatedAt)

  const handleCategoryBlur = async () => {
    const next = category.trim()
    if (next === (doc.category ?? '')) return
    try {
      await updateDocument.mutateAsync({ id: documentId, category: next || null })
    } catch (err) {
      toast.error(t('sharing.categorySaveFailed', { message: errorMessage(err) }))
    }
  }

  const changeAccess = async (level: AccessLevel, successKey: 'sharing.accessSaved' | 'sharing.publish.unpublished') => {
    try {
      await updateDocument.mutateAsync({ id: documentId, accessLevel: level })
      toast.success(t(successKey))
    } catch (err) {
      toast.error(t('sharing.accessSaveFailed', { message: errorMessage(err) }))
    }
  }

  const handleAccessChange = (level: AccessLevel) => {
    if (!accessLevel || level === accessLevel) return
    // Making a page public goes through publishing (it needs a description and renders the public page)
    if (level === 'public') {
      setPublishOpen(true)
      return
    }
    if (isPublished && !window.confirm(t('sharing.publish.changeAccessConfirm'))) return
    void changeAccess(level, 'sharing.accessSaved')
  }

  const handleUnpublish = async () => {
    if (!window.confirm(t('sharing.publish.unpublishConfirm'))) return
    try {
      await unpublishDocument.mutateAsync(documentId)
      toast.success(t('sharing.publish.unpublished'))
    } catch (err) {
      toast.error(t('sharing.accessSaveFailed', { message: errorMessage(err) }))
    }
  }

  const handleCopyPublic = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      toast.error(t('sharing.copyFailed'))
    }
  }

  return (
    <div className="space-y-5">
      <h3 className={sectionTitleClass}>{t('sharing.info')}</h3>
      {readOnly && <p className={hintClass}>{t('sharing.lockedWhileEditing')}</p>}

      <div className="space-y-1.5">
        <label htmlFor={`${id}-category`} className={labelClass}>
          {t('sharing.category')}
        </label>
        <input
          id={`${id}-category`}
          type="text"
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          onBlur={() => void handleCategoryBlur()}
          maxLength={100}
          disabled={readOnly}
          placeholder={t('sharing.categoryPlaceholder')}
          className={inputClass}
        />
      </div>

      <div className="space-y-1.5">
        <label htmlFor={`${id}-access`} className={labelClass}>
          {t('documents.accessLevel')}
        </label>
        <select
          id={`${id}-access`}
          value={accessLevel ?? ''}
          onChange={(e) => handleAccessChange(e.target.value as AccessLevel)}
          disabled={busy || readOnly}
          className={inputClass}
        >
          {!accessLevel && (
            <option value="" disabled>
              {t('common.loading')}
            </option>
          )}
          {ACCESS_LEVELS.map((level) => (
            <option key={level} value={level}>
              {t(ACCESS_LEVEL_LABEL_KEYS[level])}
            </option>
          ))}
        </select>
      </div>

      <section aria-labelledby={`${id}-publish`} className="space-y-2">
        <h4 id={`${id}-publish`} className="flex items-center gap-1.5 text-sm font-medium text-fg">
          <Globe className="h-4 w-4 text-fg-subtle" aria-hidden="true" />
          {t('sharing.publish.title')}
        </h4>
        {!accessLevel ? (
          <p className={hintClass}>{t('common.loading')}</p>
        ) : isPublished ? (
          <>
            <p className={hintClass}>{t('sharing.publish.published')}</p>
            <div className="flex items-center gap-1 rounded-md border border-border bg-surface px-2 py-1">
              <span className="min-w-0 flex-1 truncate text-xs text-fg-muted" title={publicUrl}>
                {publicUrl}
              </span>
              <button
                type="button"
                onClick={() => void handleCopyPublic()}
                aria-label={t('sharing.copyPublicUrl')}
                title={t('sharing.copyPublicUrl')}
                className={iconButtonClass}
              >
                {copied ? <Check className="h-4 w-4 text-success" /> : <Copy className="h-4 w-4" />}
              </button>
              <a
                href={publicUrl}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={t('sharing.openPublicUrl')}
                title={t('sharing.openPublicUrl')}
                className={iconButtonClass}
              >
                <ExternalLink className="h-4 w-4" />
              </a>
            </div>
            <button type="button" onClick={() => void handleUnpublish()} disabled={busy || readOnly} className={cn(buttonSecondaryClass, 'w-full')}>
              {t('sharing.publish.unpublish')}
            </button>
          </>
        ) : (
          <>
            <p className={hintClass}>{t('sharing.publish.notPublished', { slug: doc.slug })}</p>
            <button type="button" onClick={() => setPublishOpen(true)} disabled={busy || readOnly} className={cn(buttonSecondaryClass, 'w-full')}>
              {t('sharing.publish.button')}
            </button>
          </>
        )}
      </section>

      {/* Until the access level is known, treat the page as private so no new link is offered early */}
      <ShareLinkDisplay documentId={documentId} accessLevel={accessLevel ?? 'private'} />

      <DocumentKnowledgeExtractionPanel documentId={documentId} isDark={legacyIsDark} />

      <dl className="space-y-1.5 text-xs">
        {created && (
          <div className="flex items-center gap-2">
            <Calendar className="h-3.5 w-3.5 text-fg-subtle" aria-hidden="true" />
            <dt className="text-fg-subtle">{t('sharing.created')}</dt>
            <dd className="ml-auto text-fg-muted">{created}</dd>
          </div>
        )}
        {updated && (
          <div className="flex items-center gap-2">
            <Calendar className="h-3.5 w-3.5 text-fg-subtle" aria-hidden="true" />
            <dt className="text-fg-subtle">{t('sharing.updated')}</dt>
            <dd className="ml-auto text-fg-muted">{updated}</dd>
          </div>
        )}
        {(doc.authorName || doc.createdBy) && (
          <div className="flex items-center gap-2">
            <User className="h-3.5 w-3.5 text-fg-subtle" aria-hidden="true" />
            <dt className="text-fg-subtle">{t('sharing.author')}</dt>
            <dd className="ml-auto max-w-[60%] truncate text-fg-muted">{doc.authorName ?? doc.createdBy}</dd>
          </div>
        )}
      </dl>

      {publishOpen && (
        <PublishDialog
          documentId={documentId}
          initialDescription={doc.description ?? ''}
          slug={doc.slug}
          onClose={() => setPublishOpen(false)}
          publish={publishDocument}
        />
      )}
    </div>
  )
}

function PublishDialog({
  documentId,
  initialDescription,
  slug,
  onClose,
  publish,
}: {
  documentId: string
  initialDescription: string
  slug: string
  onClose: () => void
  publish: ReturnType<typeof usePublishDocument>
}) {
  const t = useT()
  const id = useId()
  const [description, setDescription] = useState(initialDescription)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    const trimmed = description.trim()
    if (!trimmed) {
      setError(t('sharing.publish.descriptionRequired'))
      return
    }
    setError(null)
    try {
      await publish.mutateAsync({ documentId, description: trimmed })
      toast.success(t('sharing.publish.done'))
      onClose()
    } catch (err) {
      setError(t('sharing.publish.failed', { message: errorMessage(err) }))
    }
  }

  return (
    <Modal title={t('sharing.publish.dialogTitle')} onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        <p className="text-sm text-fg-muted">{t('sharing.publish.dialogBody', { url: publishedDocUrl(slug, window.location.origin) })}</p>
        <div className="space-y-1.5">
          <label htmlFor={`${id}-description`} className={labelClass}>
            {t('sharing.publish.description')}
          </label>
          <textarea
            id={`${id}-description`}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={DESCRIPTION_MAX}
            rows={4}
            required
            className={cn(inputClass, 'resize-y')}
          />
          <p className={hintClass}>{t('sharing.publish.descriptionHint')}</p>
        </div>
        {error && (
          <p role="alert" className={errorTextClass}>
            {error}
          </p>
        )}
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} className={buttonSecondaryClass}>
            {t('common.cancel')}
          </button>
          <button type="submit" disabled={publish.isPending} className={buttonPrimaryClass}>
            {publish.isPending ? t('sharing.publish.publishing') : t('sharing.publish.submit')}
          </button>
        </div>
      </form>
    </Modal>
  )
}
